import constate from 'constate'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { useCallback, useState } from 'react'
import dayjs from 'dayjs'
import { employeeDataApi, useGetEmployeeDetailsWithTimelineQuery } from '@/features/employee-data/employee-data.api.ts'
import { TimelineType } from '@/pages/employee-data/components/ScdDataTimeline.tsx'
import { plainToInstance } from 'class-transformer'
import { EmployeeInfo } from '@/features/employee-data/employee-data.def.ts'
import { isFetchBaseQueryHcError } from '@/utils/api.tsx'

export const [EffectiveEmployeeInfoProvider, useEffectiveEmployeeInfo] = constate(() => {
  const { employeeId } = useParams() as { employeeId: string }
  const dispatch = useDispatch()
  const [effectiveDate, setEffectiveDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const { data, isFetching, error } = useGetEmployeeDetailsWithTimelineQuery({
    employeeId,
    effectiveDate,
  })

  const getTimelineByType = useCallback((type: TimelineType) => {
    return data?.timelines[type] || []
  }, [data])

  const hasSectionAccess = useCallback((section: string) => {
    return !!data?.accessibleSections.includes(section)
  }, [data])

  const hasWriteAccess = useCallback((section: string) => {
    if (!data?.lastAccessibleDate) {
      return false
    }

    const now = dayjs().format('YYYY-MM-DD')

    return (data.lastAccessibleDate >= now) && hasSectionAccess(section)
  }, [data?.lastAccessibleDate, hasSectionAccess])

  const refresh = useCallback(() => {
    const action = employeeDataApi.util.invalidateTags([{ type: 'EmployeeInfo', id: employeeId }, {
      type: 'EmployeeActionSummary',
      id: employeeId,
    }])
    dispatch(action)
  }, [dispatch, employeeId])

  const transformedData = plainToInstance(EmployeeInfo, data?.employee)
  const isNoAccess = isFetchBaseQueryHcError(error) ? error.status === 403 : false

  return {
    employeeId,
    effectiveDate,
    setEffectiveDate,
    isLoading: isFetching,
    isNoAccess,
    employee: transformedData,
    employeeActions: data?.employeeActions || [],
    getTimelineByType,
    hasSectionAccess,
    hasWriteAccess,
    lastAccessibleDate: data?.lastAccessibleDate,
    currentEmployeeAccess: data?.currentAccess,
    refresh,
  }
})
